import React, { memo, useCallback, useEffect, useMemo } from 'react';

import FormGeneratorSwitchCDS from '@sharedCR/coreDesignSystem/formGenerator/FormGeneratorSwitchCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  defaultFloodServiceOperationOption,
  FieldTypesEnum,
  floodServiceDeterminationKinds,
  FloodServiceKind,
  floodServiceOperationOptions,
} from 'constants/loanActionEditor';
import { setIn } from 'utils/data';

import { loanManagementTestIds } from '../loanManagement.testIds';

import useFormContext from './hooks/useFormContext';
import { COMPANY_DEFAULT_FLOOD_PRODUCT_OPTION } from './LoanActionFloodServiceRow.constants';
import { getFloodProductOverrideOptions } from './LoanActionFloodServiceRow.utils';
import { DynamicValueInput } from './LoanActionItems';

const emptyFloodProductOptions = [];

// Split out of LoanActionReportRow to keep that component under the max-lines-per-function
// lint limit — the ServiceLink Operation/Product/Rush/Allow-manual-selection fields for the
// Flood Elevation Certificate report row, only rendered when that report's Vendor is ServiceLink.
const LoanActionServiceLinkFields = ({
  fieldPath,
  disabled = false,
  isAutomatedActions = false,
  floodProductOptions = emptyFloodProductOptions,
}) => {
  const { form, setInForm, setForm } = useFormContext();

  const operationFieldPath = `${fieldPath}.serviceLinkOperation`;
  const productOverrideFieldPath = `${fieldPath}.serviceLinkProductOverride`;
  const rushFieldPath = `${fieldPath}.serviceLinkRush`;
  const allowManualSelectionFieldPath = `${fieldPath}.serviceLinkAllowManualSelection`;

  const operation = _.get(form, operationFieldPath);
  const rush = _.get(form, rushFieldPath);
  const allowManualSelection = _.get(form, allowManualSelectionFieldPath);

  // Product override + the execution-time prompt apply only to a Determination order; Rush also
  // covers a Life-of-Loan Upgrade — mirrors LoanActionFloodServiceRow's per-operation gating.
  const isDeterminationKind = operation?.value === FloodServiceKind.DETERMINATION;
  const isDetermination = floodServiceDeterminationKinds.includes(operation?.value);
  // The execution-time prompt defers Product + Rush to the operator, so both hide while it is on.
  const isRushHidden = isDeterminationKind && !!allowManualSelection;

  const productOverrideOptions = useMemo(
    () => getFloodProductOverrideOptions(floodProductOptions),
    [floodProductOptions],
  );

  const onChangeOperation = useCallback(
    (__, value) => {
      // Reset every operation-gated field on change, so a hidden Rush / Allow-manual value from a
      // prior operation can't persist into the saved blob (the serializer gates them, but keep the
      // form clean too). Mirrors LoanActionFloodServiceRow.onChangeKind.
      setForm(prev => {
        const reset = _.omit(prev, [
          productOverrideFieldPath,
          rushFieldPath,
          allowManualSelectionFieldPath,
        ]);

        return value?.value === FloodServiceKind.DETERMINATION
          ? setIn(reset, productOverrideFieldPath, COMPANY_DEFAULT_FLOOD_PRODUCT_OPTION)
          : reset;
      });
    },
    [setForm, productOverrideFieldPath, rushFieldPath, allowManualSelectionFieldPath],
  );

  const onToggleAllowManualSelection = useCallback(
    (__, checked) => {
      // Turning the prompt on hides Product + Rush; clear Rush and re-seed Product to the Company
      // default so both are valid again when it is turned back off. Mirrors onTogglePromptAtExecution.
      setInForm(allowManualSelectionFieldPath, checked);
      setForm(prev =>
        setIn(
          _.omit(prev, [rushFieldPath]),
          productOverrideFieldPath,
          COMPANY_DEFAULT_FLOOD_PRODUCT_OPTION,
        ),
      );
    },
    [setInForm, setForm, allowManualSelectionFieldPath, rushFieldPath, productOverrideFieldPath],
  );

  const onToggleRush = useCallback(
    (__, checked) => setInForm(rushFieldPath, checked),
    [setInForm, rushFieldPath],
  );

  useEffect(() => {
    if (!_.isNil(operation)) {
      return;
    }

    // The default operation is always Determination (floodServiceOperationOptions[0]), so the
    // product must resolve to the visible Company-default option, mirroring onChangeOperation.
    setInForm(operationFieldPath, defaultFloodServiceOperationOption);
    setInForm(productOverrideFieldPath, COMPANY_DEFAULT_FLOOD_PRODUCT_OPTION);
  }, [operation, operationFieldPath, productOverrideFieldPath, setInForm]);

  return (
    <>
      <DynamicValueInput
        required
        disabled={disabled}
        label="Operation"
        name={operationFieldPath}
        options={floodServiceOperationOptions}
        type={FieldTypesEnum.SELECT}
        onChange={onChangeOperation}
      />
      {isDeterminationKind && !isAutomatedActions && (
        <FormGeneratorSwitchCDS
          dataTestId={loanManagementTestIds.getFloodPromptAtExecutionSwitchId(fieldPath)}
          disabled={disabled}
          label="Allow order options at execution time"
          name={allowManualSelectionFieldPath}
          value={!!allowManualSelection}
          onChange={onToggleAllowManualSelection}
        />
      )}
      {isDetermination && !isRushHidden && (
        <FormGeneratorSwitchCDS
          dataTestId={loanManagementTestIds.getFloodRushSwitchId(fieldPath)}
          disabled={disabled}
          label="Rush (high-priority) order"
          name={rushFieldPath}
          value={!!rush}
          onChange={onToggleRush}
        />
      )}
      {isDeterminationKind && !allowManualSelection && (
        <DynamicValueInput
          disabled={disabled}
          label="Product"
          name={productOverrideFieldPath}
          options={productOverrideOptions}
          type={FieldTypesEnum.SELECT}
        />
      )}
    </>
  );
};

LoanActionServiceLinkFields.propTypes = {
  fieldPath: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  isAutomatedActions: PropTypes.bool,
  floodProductOptions: PropTypes.array,
};

export default memo(LoanActionServiceLinkFields);
